import React, { useState, useEffect } from "react";
import { Button, Card, CardBody, CardHeader } from "reactstrap";
import { formatCurrency } from "../../../../utils/formatCurrency";
import LeafIcon from "../../../../components/LeafIcon";
import FonteBadge from "../../../../components/cotacao/FonteBadge";
import FreteSpotForm from "../../../../components/cotacao/FreteSpotForm";
import {
  autopilotBodyStyle,
  autopilotCardStyle,
  autopilotDescriptionStyle,
  autopilotEyebrowStyle,
  autopilotHeaderStyle,
  autopilotInsetCardStyle,
  autopilotSecondaryButtonStyle,
} from "../../../../components/autopilot/styles";

const panelShellStyle = {
  ...autopilotCardStyle,
  display: "flex",
  flexDirection: "column",
  background: "var(--everlog-card-hero-bg)",
};

// PO1-3200: a escolha passa a ser pela proposta (transportadora + origem do
// preço), não só pelo nome. Com o "+ Frete Spot" a mesma transportadora pode
// aparecer por tabela e por spot, e o nome sozinho não diz qual das duas.
const proposalKey = (p) =>
  `${p.idTransportadora ?? "sem-id"}||${p.fonte || "tabela"}||${p.transportadora}`;

const ProposalPanel = ({
  embarque,
  proposals,
  indicador,
  onClose,
  onApply,
  onOpenFullQuote,
  idEmbarcador,
  onAddSpot,
  onRemoveSpot,
}) => {
  const sortedProposals = [...proposals].sort((a, b) => a.valor - b.valor);

  // A escolha já gravada no embarque vem só com o nome: casa com a primeira
  // proposta daquele nome.
  const keyDaEscolhaGravada = () => {
    const nome = embarque.transportadoraEscolhida;
    if (!nome) return "";
    const p = sortedProposals.find((x) => x.transportadora === nome);
    return p ? proposalKey(p) : "";
  };

  const [chosen, setChosen] = useState(keyDaEscolhaGravada);
  const [spotAberto, setSpotAberto] = useState(false);

  useEffect(() => {
    setChosen(keyDaEscolhaGravada());
    setSpotAberto(false);
  }, [embarque._id]); // eslint-disable-line react-hooks/exhaustive-deps

  const chosenProposal = sortedProposals.find((p) => proposalKey(p) === chosen) || null;
  const cidade = [embarque.cidadeDestino, embarque.ufDestino].filter(Boolean).join(" / ");
  const summaryLine = [embarque.cliente, cidade].filter(Boolean).join(" · ");

  // Indicadores (chegam do backend após a simulação; podem ainda não ter
  // carregado, ou o embarcador não ter opt-in ANTT).
  const anttHabilitado = Boolean(indicador && indicador.antt);
  const anttEstimado = anttHabilitado ? Boolean(indicador.antt.isEstimado) : false;
  const carbono = indicador && indicador.carbono ? indicador.carbono : null;
  const anttPorTransp = {};
  if (anttHabilitado && Array.isArray(indicador.antt.propostas)) {
    indicador.antt.propostas.forEach((p) => {
      if (p && p.idTransportadora != null) anttPorTransp[p.idTransportadora] = p;
    });
  }

  // Pill de conformidade ANTT por transportadora (verde Conforme / vermelho
  // Abaixo). "est." porque na cotação eixos/distância são estimados.
  const renderAnttPill = (idTransportadora) => {
    if (!anttHabilitado) return null;
    const a = anttPorTransp[idTransportadora];
    if (!a || !a.status || a.status === "nao_calculado") {
      return (
        <span className="antt-pill antt-pill--nc" title="Piso ANTT não calculado">
          —
        </span>
      );
    }
    const piso = indicador.antt.pisoAntt;
    const idx = a.indiceAntt != null ? ` · índice ${a.indiceAntt.toFixed(2)}` : "";
    const title =
      (piso ? `Piso ANTT ${formatCurrency(piso)}` : "Piso ANTT") +
      idx +
      (anttEstimado ? " (estimado)" : "");
    return (
      <span className={`antt-pill antt-pill--${a.status}`} title={title}>
        {a.status === "conforme" ? "Conforme" : "Abaixo"}
        {anttEstimado ? <span className="antt-pill__est"> est.</span> : null}
      </span>
    );
  };

  return (
    <Card className="card-everlog-shell proposal-panel" style={panelShellStyle}>
      <CardHeader className="proposal-panel__header" style={autopilotHeaderStyle}>
        <div>
          <div style={autopilotEyebrowStyle}>Detalhe lateral</div>
          <h3 className="proposal-panel__title">Embarque {embarque.numEmbarque}</h3>
          <p className="proposal-panel__description" style={autopilotDescriptionStyle}>
            {summaryLine || "Sem identificação complementar para este embarque."}
          </p>
        </div>
        <button className="proposal-panel__close" onClick={onClose} type="button">
          ×
        </button>
      </CardHeader>

      <CardBody className="proposal-panel__body" style={autopilotBodyStyle}>
        <div className="proposal-panel__stack">
          <div className="proposal-panel__summary" style={autopilotInsetCardStyle}>
            <div className="proposal-panel__summary-grid">
              <div className="proposal-panel__summary-row">
                <span className="proposal-panel__summary-label">Destinatário</span>
                <strong>{embarque.cliente || "—"}</strong>
              </div>
              <div className="proposal-panel__summary-row">
                <span className="proposal-panel__summary-label">Cidade</span>
                <strong>{cidade || "—"}</strong>
              </div>
              <div className="proposal-panel__summary-row">
                <span className="proposal-panel__summary-label">Valor NF</span>
                <strong>{embarque.valorNF || "—"}</strong>
              </div>
              <div className="proposal-panel__summary-row">
                <span className="proposal-panel__summary-label">KG / M3</span>
                <strong>
                  {embarque.pesoBruto || "—"} / {embarque.densidade || "—"}
                </strong>
              </div>
              {carbono && carbono.emissaoKgCO2 != null && (
                <div className="proposal-panel__summary-row">
                  <span className="proposal-panel__summary-label">Emissão CO₂</span>
                  <strong className="proposal-panel__carbono" title="Estimativa para o trajeto (rodoviário)">
                    <LeafIcon />~{Math.round(carbono.emissaoKgCO2)} kg
                    {indicador && indicador.isEstimado ? (
                      <span className="antt-pill__est"> est.</span>
                    ) : null}
                  </strong>
                </div>
              )}
            </div>
          </div>

          <div className="proposal-panel__proposals" style={autopilotInsetCardStyle}>
            <div
              className="proposal-panel__section-header"
              style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}
            >
              <span>
                Propostas {sortedProposals.length > 0 ? `(${sortedProposals.length})` : ""}
              </span>
              {onAddSpot && !spotAberto && (
                <button
                  type="button"
                  className="proposal-panel__link proposal-panel__spot-btn"
                  onClick={() => setSpotAberto(true)}
                  title="Incluir transportadora da carteira com valor negociado"
                >
                  + Frete Spot
                </button>
              )}
            </div>
            <div className="proposal-panel__section-body">
              {spotAberto && (
                <FreteSpotForm
                  idEmbarcador={idEmbarcador}
                  onAdd={(proposta) => {
                    onAddSpot(embarque._id, proposta);
                    setChosen(proposalKey(proposta));
                    setSpotAberto(false);
                  }}
                  onCancel={() => setSpotAberto(false)}
                />
              )}
              {sortedProposals.length === 0 ? (
                <p className="proposal-panel__empty">
                  Nenhuma proposta disponível. Simule o frete ou use "+ Frete Spot" para
                  incluir uma transportadora com valor negociado.
                </p>
              ) : (
                <table className="proposals-table">
                  <tbody>
                    {sortedProposals.map((p, i) => (
                      <tr
                        // PO1-3178: a chave era só o nome e duplicava quando a mesma
                        // transportadora vinha por tabela E por api. O backend agora
                        // deduplica, mas um conector ainda pode devolver dois serviços
                        // (Correios PAC e SEDEX) — o id sozinho também não basta.
                        key={proposalKey(p)}
                        onClick={() => setChosen(proposalKey(p))}
                        className={chosen === proposalKey(p) ? "selected" : ""}
                      >
                        <td className="proposal-panel__transportadora">
                          {i === 0 && <span className="badge-best">★</span>}
                          {p.transportadora}
                          {p.fonte ? <FonteBadge fonte={p.fonte} /> : null}
                        </td>
                        <td className="proposal-panel__valor" style={{ textAlign: "right" }}>
                          {formatCurrency(p.valor)}
                        </td>
                        <td className="proposal-panel__prazo" style={{ textAlign: "right" }}>
                          {p.prazo != null ? `${p.prazo}d` : "—"}
                        </td>
                        {anttHabilitado && (
                          <td className="proposal-panel__antt" style={{ textAlign: "right" }}>
                            {renderAnttPill(p.idTransportadora)}
                          </td>
                        )}
                        <td className="proposal-panel__acao" style={{ textAlign: "right", width: 24 }}>
                          {p.fonte === "spot" && onRemoveSpot ? (
                            <button
                              type="button"
                              className="proposal-panel__spot-remover"
                              title="Remover frete spot"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (chosen === proposalKey(p)) setChosen("");
                                onRemoveSpot(embarque._id, p);
                              }}
                            >
                              ✕
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <div className="proposal-panel__chosen" style={autopilotInsetCardStyle}>
            <label>Transportadora escolhida</label>
            <select value={chosen} onChange={(e) => setChosen(e.target.value)}>
              <option value="">— Selecione —</option>
              {sortedProposals.map((p) => (
                <option key={proposalKey(p)} value={proposalKey(p)}>
                  {p.transportadora}
                  {p.fonte === "spot" ? " (spot)" : ""} — {formatCurrency(p.valor)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="proposal-panel__actions">
          <Button
            className="btn-sm"
            style={autopilotSecondaryButtonStyle}
            block
            disabled={!chosenProposal}
            onClick={() => onApply(embarque._id, chosenProposal.transportadora, chosenProposal)}
          >
            Aplicar seleção
          </Button>
          <button
            className="proposal-panel__link"
            type="button"
            onClick={() => onOpenFullQuote(embarque._id)}
          >
            Fechar painel
          </button>
        </div>
      </CardBody>
    </Card>
  );
};

export default ProposalPanel;
